import React, { createContext, useContext, useState, useRef, ReactNode, useCallback } from 'react';
import {
  ownerService,
  OwnerMetrics,
  TenantSummary,
  TenantDetails,
  Invoice,
  BillingSummary,
  SystemHealth,
  UsageAnalytics,
  GrowthAnalytics
} from '../services/ownerService';

// State Interface
interface OwnerState {
  // Dashboard
  metrics: OwnerMetrics | null;
  recentActivity: Array<{
    id: string;
    name: string;
    createdAt: string;
    status: string;
    package: string;
  }>;
  
  // Client Management
  tenants: TenantSummary[];
  currentTenant: TenantDetails | null;
  tenantsPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  
  // Billing
  invoices: Invoice[];
  billingPagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  billingSummary: BillingSummary | null;
  
  // System Health
  systemHealth: SystemHealth | null;
  
  // Analytics
  usageAnalytics: UsageAnalytics[];
  growthAnalytics: GrowthAnalytics | null;
  
  // Loading states
  loading: {
    dashboard: boolean;
    tenants: boolean;
    tenant: boolean;
    billing: boolean;
    systemHealth: boolean;
    analytics: boolean;
  };
  
  // Error states
  errors: {
    dashboard: string | null;
    tenants: string | null;
    tenant: string | null;
    billing: string | null;
    systemHealth: string | null;
    analytics: string | null;
  };
}

// Actions Interface
interface OwnerActions {
  // Dashboard
  loadDashboardMetrics: () => Promise<void>;
  
  // Client Management
  loadTenants: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    package?: string;
    search?: string;
  }) => Promise<void>;
  loadTenant: (id: string) => Promise<void>;
  createTenant: (data: {
    name: string;
    email: string;
    password: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  }) => Promise<{
    message: string;
    tenant: TenantSummary;
    adminUser: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
    };
  }>;
  updateTenant: (id: string, data: {
    status?: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
  }) => Promise<void>;
  
  // Billing
  loadBilling: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => Promise<void>;
  loadBillingSummary: () => Promise<void>;
  
  // System Health
  loadSystemHealth: () => Promise<void>;
  createAlert: (alert: {
    type: 'ERROR' | 'WARNING' | 'INFO';
    message: string;
  }) => Promise<void>;
  resolveAlert: (id: string) => Promise<void>;
  
  // Analytics
  loadUsageAnalytics: (period?: '7d' | '30d' | '90d') => Promise<void>;
  loadGrowthAnalytics: () => Promise<void>;
  
  // Utility
  clearErrors: () => void;
  clearCurrentTenant: () => void;
}

interface OwnerContextType extends OwnerState, OwnerActions {}

// Initial State
const initialState: OwnerState = {
  // Dashboard
  metrics: null,
  recentActivity: [],
  
  // Client Management
  tenants: [],
  currentTenant: null,
  tenantsPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  },
  
  // Billing
  invoices: [],
  billingPagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  },
  billingSummary: null,
  
  // System Health
  systemHealth: null,
  
  // Analytics
  usageAnalytics: [],
  growthAnalytics: null,
  
  // Loading states
  loading: {
    dashboard: false,
    tenants: false,
    tenant: false,
    billing: false,
    systemHealth: false,
    analytics: false
  },
  
  // Error states
  errors: {
    dashboard: null,
    tenants: null,
    tenant: null,
    billing: null,
    systemHealth: null,
    analytics: null
  }
};

// Create Context
const OwnerContext = createContext<OwnerContextType | undefined>(undefined);

// Provider Component
export const OwnerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OwnerState>(initialState);
  const loadingRef = useRef<Set<string>>(new Set());

  // Helper to prevent duplicate calls
  const preventDuplicateCall = (key: string): boolean => {
    if (loadingRef.current.has(key)) {
      return false;
    }
    loadingRef.current.add(key);
    return true;
  };

  const finishCall = (key: string) => {
    loadingRef.current.delete(key);
  };

  // Helper function to handle errors
  const handleError = (section: keyof OwnerState['errors'], error: any) => {
    console.error(`Owner ${section} error:`, error);
    setState(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [section]: error.response?.data?.message || error.message || 'An error occurred'
      }
    }));
  };

  // Dashboard Actions
  const loadDashboardMetrics = useCallback(async () => {
    if (!preventDuplicateCall('dashboard')) return;

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, dashboard: true },
      errors: { ...prev.errors, dashboard: null }
    }));

    try {
      const data = await ownerService.getDashboardMetrics();
      setState(prev => ({
        ...prev,
        metrics: data.metrics,
        recentActivity: data.recentActivity,
        loading: { ...prev.loading, dashboard: false }
      }));
    } catch (error) {
      handleError('dashboard', error);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, dashboard: false }
      }));
    } finally {
      finishCall('dashboard');
    }
  }, []);

  // Client Management Actions
  const loadTenants = useCallback(async (params = {}) => {
    if (!preventDuplicateCall('tenants')) return;

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, tenants: true },
      errors: { ...prev.errors, tenants: null }
    }));

    try {
      const data = await ownerService.getTenants(params);
      setState(prev => ({
        ...prev,
        tenants: data.tenants,
        tenantsPagination: data.pagination,
        loading: { ...prev.loading, tenants: false }
      }));
    } catch (error) {
      handleError('tenants', error);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, tenants: false }
      }));
    } finally {
      finishCall('tenants');
    }
  }, []);

  const loadTenant = async (id: string) => {
    if (!preventDuplicateCall(`tenant-${id}`)) return;

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, tenant: true },
      errors: { ...prev.errors, tenant: null }
    }));

    try {
      const tenant = await ownerService.getTenant(id);
      setState(prev => ({
        ...prev,
        currentTenant: tenant,
        loading: { ...prev.loading, tenant: false }
      }));
    } catch (error) {
      handleError('tenant', error);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, tenant: false }
      }));
    } finally {
      finishCall(`tenant-${id}`);
    }
  };

  const createTenant = useCallback(async (data: {
    name: string;
    email: string;
    password: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
  }) => {
    try {
      const result = await ownerService.createTenant(data);
      
      // Add the new tenant to the list
      setState(prev => ({
        ...prev,
        tenants: [result.tenant, ...prev.tenants]
      }));

      return result;
    } catch (error) {
      handleError('tenants', error);
      throw error;
    }
  }, []);

  const updateTenant = useCallback(async (id: string, data: {
    status?: string;
    package?: string;
    storageLimit?: number;
    userLimit?: number;
  }) => {
    try {
      const updatedTenant = await ownerService.updateTenant(id, data);
      
      setState(prev => ({
        ...prev,
        tenants: prev.tenants.map(tenant => 
          tenant.id === id ? { ...tenant, ...updatedTenant } : tenant
        )
      }));

      if (state.currentTenant?.id === id) {
        await loadTenant(id);
      }
    } catch (error) {
      handleError('tenants', error);
      throw error;
    }
  }, [state.currentTenant?.id]);

  // System Health Actions
  const loadSystemHealth = useCallback(async () => {
    if (!preventDuplicateCall('systemHealth')) return;

    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, systemHealth: true },
      errors: { ...prev.errors, systemHealth: null }
    }));

    try {
      const health = await ownerService.getSystemHealth();
      setState(prev => ({
        ...prev,
        systemHealth: health,
        loading: { ...prev.loading, systemHealth: false }
      }));
    } catch (error) {
      handleError('systemHealth', error);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, systemHealth: false }
      }));
    } finally {
      finishCall('systemHealth');
    }
  }, []);

  // Simplified implementations for other methods
  const loadBilling = async (params = {}) => {
    // Implementation similar to loadTenants
  };

  const loadBillingSummary = async () => {
    // Implementation similar to loadDashboardMetrics
  };

  const createAlert = async (alert: { type: 'ERROR' | 'WARNING' | 'INFO'; message: string }) => {
    await ownerService.createAlert(alert);
    await loadSystemHealth();
  };

  const resolveAlert = async (id: string) => {
    await ownerService.resolveAlert(id);
    await loadSystemHealth();
  };

  const loadUsageAnalytics = async (period: '7d' | '30d' | '90d' = '30d') => {
    // Implementation similar to other load methods
  };

  const loadGrowthAnalytics = async () => {
    // Implementation similar to other load methods
  };

  const clearErrors = useCallback(() => {
    setState(prev => ({
      ...prev,
      errors: {
        dashboard: null,
        tenants: null,
        tenant: null,
        billing: null,
        systemHealth: null,
        analytics: null
      }
    }));
  }, []);

  const clearCurrentTenant = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentTenant: null
    }));
  }, []);

  const contextValue: OwnerContextType = {
    ...state,
    loadDashboardMetrics,
    loadTenants,
    loadTenant,
    createTenant,
    updateTenant,
    loadBilling,
    loadBillingSummary,
    loadSystemHealth,
    createAlert,
    resolveAlert,
    loadUsageAnalytics,
    loadGrowthAnalytics,
    clearErrors,
    clearCurrentTenant
  };

  return (
    <OwnerContext.Provider value={contextValue}>
      {children}
    </OwnerContext.Provider>
  );
};

// Hook
export const useOwner = (): OwnerContextType => {
  const context = useContext(OwnerContext);
  if (context === undefined) {
    throw new Error('useOwner must be used within an OwnerProvider');
  }
  return context;
}; 